import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { RestaurantProvider, useRestaurant } from '../../context/RestaurantContext';
import { 
  LayoutDashboard, UtensilsCrossed, Package, Calendar, Settings, 
  Users, Menu, X, Bell, LogOut, ChevronDown, Scan, MapPin, Grid3X3, Coffee,
  HelpCircle, Store, Loader2, Globe, BarChart3, Monitor, ChefHat,
  Boxes, Gift, FileText, Printer, Shield, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { CurrencySelector } from '../../components/CurrencySelector';
import { NotificationCenter } from '../../components/NotificationCenter';
import CreateRestaurantForm from './CreateRestaurantForm';

const languages = [
  { code: 'en', name: 'English', flag: '/flags/gb.svg' },
  { code: 'es', name: 'Español', flag: '/flags/es.svg' },
  { code: 'ar', name: 'العربية', flag: '/flags/sa.svg' },
  { code: 'hi', name: 'हिन्दी', flag: '/flags/in.svg' },
  { code: 'pa', name: 'ਪੰਜਾਬੀ', flag: '/flags/pa.svg' },
];

function RestaurantLayoutContent() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { hasRestaurant, loading, selectedRestaurant } = useRestaurant();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('restaurant_sidebar_collapsed');
    return saved === 'true';
  });

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('restaurant_sidebar_collapsed', sidebarCollapsed.toString());
  }, [sidebarCollapsed]);

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];
  
  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
  };

  // Menu items - POS is highlighted (orange), others are normal (blue when active)
  const menuItems = [
    { path: '/restaurant', icon: LayoutDashboard, label: t('restaurantAdmin.dashboard') },
    { path: '/restaurant/pos', icon: Monitor, label: t('restaurantAdmin.posTill') || 'POS Till', isPOS: true },
    { path: '/restaurant/kitchen', icon: ChefHat, label: t('restaurantAdmin.kitchenDisplay') || 'Kitchen Display' },
    { path: '/restaurant/menu', icon: UtensilsCrossed, label: t('restaurantAdmin.menuManagement') },
    { path: '/restaurant/stock', icon: Boxes, label: t('restaurantAdmin.stockManagement') || 'Stock Management' },
    { path: '/restaurant/orders', icon: Package, label: t('restaurantAdmin.orders') },
    { path: '/restaurant/drivers', icon: MapPin, label: t('restaurantAdmin.driverMap') },
    { path: '/restaurant/table-management', icon: Grid3X3, label: t('restaurantAdmin.tableManagement') },
    { path: '/restaurant/tables', icon: Calendar, label: t('restaurantAdmin.tableBookings') },
    { path: '/restaurant/live-tables', icon: UtensilsCrossed, label: 'Live Tables', isHighlight: true },
    { path: '/restaurant/staff', icon: Users, label: t('restaurantAdmin.staffManagement') || 'Staff Management' },
    { path: '/restaurant/waiters', icon: Coffee, label: t('restaurantAdmin.waiterManagement') },
    { path: '/restaurant/loyalty', icon: Gift, label: t('restaurantAdmin.loyaltyProgram') || 'Loyalty Program' },
    { path: '/restaurant/reports', icon: BarChart3, label: t('restaurantAdmin.financialReports') || 'Financial Reports' },
    { path: '/restaurant/eod-reports', icon: FileText, label: t('restaurantAdmin.endOfDay') || 'End of Day' },
    { path: '/restaurant/print-settings', icon: Printer, label: t('restaurantAdmin.printSettings') || 'Print Settings' },
    { path: '/restaurant/verifactu', icon: Shield, label: t('restaurantAdmin.verifactu') || 'VeriFactu' },
    { path: '/restaurant/settings', icon: Settings, label: t('restaurantAdmin.settings') },
    { path: '/restaurant/support', icon: HelpCircle, label: t('restaurantAdmin.support') },
  ];

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-slate-500">{t('restaurantAdmin.loadingRestaurant')}</p>
        </div>
      </div>
    );
  }

  // Show create restaurant form if no restaurant
  if (!hasRestaurant) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Simple header for create flow */}
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <Link to="/" className="flex items-center gap-2">
              <svg viewBox="0 0 100 100" className="w-10 h-10">
                <rect width="100" height="100" rx="20" fill="#F97316"/>
                <g fill="white">
                  <rect x="18" y="25" width="4" height="50" rx="2"/>
                  <rect x="25" y="25" width="3" height="20" rx="1.5"/>
                  <rect x="18" y="25" width="3" height="20" rx="1.5"/>
                  <rect x="11" y="25" width="3" height="20" rx="1.5"/>
                  <ellipse cx="78" cy="35" rx="12" ry="15"/>
                  <rect x="76" y="45" width="4" height="30" rx="2"/>
                  <rect x="30" y="50" width="44" height="8" rx="4"/>
                </g>
              </svg>
              <span className="font-bold text-lg text-slate-900">Hambre</span>
            </Link>

            <div className="flex items-center gap-4">
              {/* Language Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-1 px-2" data-testid="language-selector">
                    <img src={currentLang.flag} alt={currentLang.name} className="w-5 h-4 object-cover rounded-sm" />
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[160px]">
                  {languages.map((lang) => (
                    <DropdownMenuItem 
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`flex items-center gap-3 ${i18n.language === lang.code ? 'bg-orange-50 text-orange-600' : ''}`}
                    >
                      <img src={lang.flag} alt={lang.name} className="w-5 h-4 object-cover rounded-sm" />
                      <span>{lang.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              
              <span className="text-sm text-slate-500">{t('restaurantAdmin.welcome')}, {user?.name}</span>
              <Button variant="ghost" size="sm" onClick={logout}>
                <LogOut className="w-4 h-4 mr-2" />
                {t('restaurantAdmin.logout')}
              </Button>
            </div>
          </div>
        </header>

        <main className="p-4 lg:p-8">
          <CreateRestaurantForm />
        </main>
      </div>
    );
  }

  // Normal layout with sidebar when restaurant exists
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-screen bg-slate-900 text-white z-40 flex flex-col transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0
          ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'}`}
        data-testid="restaurant-sidebar"
      >
        {/* Header with logo */}
        <div className="p-4 border-b border-slate-800 flex-shrink-0">
          <Link to="/" className="flex items-center gap-2">
            <svg viewBox="0 0 100 100" className="w-10 h-10 flex-shrink-0">
              <rect width="100" height="100" rx="20" fill="#F97316"/>
              <g fill="white">
                <rect x="18" y="25" width="4" height="50" rx="2"/>
                <rect x="25" y="25" width="3" height="20" rx="1.5"/>
                <rect x="18" y="25" width="3" height="20" rx="1.5"/>
                <rect x="11" y="25" width="3" height="20" rx="1.5"/>
                <ellipse cx="78" cy="35" rx="12" ry="15"/>
                <rect x="76" y="45" width="4" height="30" rx="2"/>
                <rect x="30" y="50" width="44" height="8" rx="4"/>
              </g>
            </svg>
            {!sidebarCollapsed && (
              <span className="font-bold text-lg whitespace-nowrap">Hambre</span>
            )}
          </Link>
        </div>

        {/* Collapse/Expand button - desktop only */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 bg-slate-700 hover:bg-slate-600 rounded-full items-center justify-center text-white shadow-md transition-colors z-50"
          data-testid="sidebar-collapse-btn"
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </button>

        {/* Restaurant name indicator */}
        {selectedRestaurant && !sidebarCollapsed && (
          <div className="px-4 py-3 border-b border-slate-800">
            <div className="flex items-center gap-2 text-slate-300">
              <Store className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm font-medium truncate">{selectedRestaurant.name}</span>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="p-2 space-y-1 flex-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            // POS gets orange styling, everything else gets blue
            const activeClass = item.isPOS 
              ? 'bg-orange-500 text-white' 
              : 'bg-blue-600 text-white';
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mx-1 transition-colors duration-200
                  ${isActive 
                    ? activeClass
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }
                  ${sidebarCollapsed ? 'justify-center' : ''}`}
                onClick={() => setSidebarOpen(false)}
                title={sidebarCollapsed ? item.label : ''}
                data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!sidebarCollapsed && (
                  <span className="whitespace-nowrap overflow-hidden">{item.label}</span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Logout button */}
        <div className="p-2 border-t border-slate-800 flex-shrink-0">
          <Button
            variant="ghost"
            className={`w-full text-slate-400 hover:text-white hover:bg-slate-800 ${sidebarCollapsed ? 'justify-center px-2' : 'justify-start'}`}
            onClick={logout}
            title={sidebarCollapsed ? t('restaurantAdmin.logout') : ''}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!sidebarCollapsed && <span className="ml-3">{t('restaurantAdmin.logout')}</span>}
          </Button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className={`transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        {/* Top header */}
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
              <h1 className="text-lg font-semibold text-slate-900">{t('restaurantAdmin.panel')}</h1>
            </div>

            <div className="flex items-center gap-4">
              {/* Language Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-1 px-2" data-testid="language-selector-main">
                    <img src={currentLang.flag} alt={currentLang.name} className="w-5 h-4 object-cover rounded-sm" />
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[160px]">
                  {languages.map((lang) => (
                    <DropdownMenuItem 
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`flex items-center gap-3 ${i18n.language === lang.code ? 'bg-orange-50 text-orange-600' : ''}`}
                    >
                      <img src={lang.flag} alt={lang.name} className="w-5 h-4 object-cover rounded-sm" />
                      <span>{lang.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Currency Selector */}
              <CurrencySelector />
              
              {/* Notification Center */}
              <NotificationCenter />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                      <span className="text-sm font-semibold text-orange-600">
                        {user?.name?.charAt(0) || 'R'}
                      </span>
                    </div>
                    <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to="/restaurant/settings">{t('restaurantAdmin.settings')}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} className="text-red-600">
                    {t('restaurantAdmin.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// Wrap with provider
export default function RestaurantLayout() {
  return (
    <RestaurantProvider>
      <RestaurantLayoutContent />
    </RestaurantProvider>
  );
}
